/**
 * Iter 229 · Wave 4 admin console — AirQ · Schemes · ABDM uplift · Happiness
 *
 * Four sub-modules gated behind tabs, each hitting the /api/wave4/* router.
 */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  AirplaneTilt, Bank, ShieldCheck, Smiley, Plus, PlayCircle,
  Airplane, MapPin, Coins, Clock,
} from "@phosphor-icons/react";

const TABS = [
  { key: "airq",      label: "AirQ · Air/Drone",  icon: AirplaneTilt },
  { key: "schemes",   label: "Schemes · Karunya", icon: Bank },
  { key: "abdm",      label: "ABDM M1/M2",        icon: ShieldCheck },
  { key: "happiness", label: "Happiness Arm",     icon: Smiley },
];

export default function Wave4Console() {
  const [tab, setTab] = useState("airq");
  return (
    <AppShell title="Wave 4" subtitle="AirQ · Schemes · ABDM uplift · Happiness Arm">
      <div className="space-y-4" data-testid="wave4-root">
        <div className="flex items-center gap-2 border-b border-[#E5E2D9] overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.icon; const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                data-testid={`wave4-tab-${t.key}`}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap ${
                  active ? "border-[#0F766E] text-[#0F766E]" : "border-transparent text-[#5C5A55]"
                }`}>
                <Icon size={14} weight={active?"fill":"regular"}/>{t.label}
              </button>
            );
          })}
        </div>
        {tab === "airq"      && <AirqTab/>}
        {tab === "schemes"   && <SchemesTab/>}
        {tab === "abdm"      && <AbdmUpliftTab/>}
        {tab === "happiness" && <HappinessTab/>}
      </div>
    </AppShell>
  );
}


// =====================================================
// 1. AirQ  (Iter 230 — Overview · Cases · Crew · Compliance · Approvals)
// =====================================================
const AIRQ_SUBTABS = [
  { key: "overview",   label: "Overview" },
  { key: "cases",      label: "Cases" },
  { key: "crew",       label: "Crew" },
  { key: "compliance", label: "Compliance" },
  { key: "approvals",  label: "Pad approvals" },
  { key: "accounts",   label: "Accounts" },
];

function AirqTab() {
  const [sub, setSub] = useState("overview");
  return (
    <div className="space-y-3" data-testid="airq-tab">
      <div className="flex items-center gap-1 border-b border-[#E5E2D9] overflow-x-auto -mb-px">
        {AIRQ_SUBTABS.map(t => (
          <button key={t.key} onClick={() => setSub(t.key)}
                    data-testid={`airq-subtab-${t.key}`}
                    className={`px-3 py-1.5 text-xs font-medium border-b-2 whitespace-nowrap ${
                      sub===t.key ? "border-[#0F766E] text-[#0F766E]"
                                       : "border-transparent text-[#5C5A55]"}`}>
            {t.label}
          </button>
        ))}
      </div>
      {sub === "overview"   && <AirqOverview/>}
      {sub === "cases"      && <AirqCases/>}
      {sub === "crew"       && <AirqCrew/>}
      {sub === "compliance" && <AirqCompliance/>}
      {sub === "approvals"  && <AirqApprovals/>}
      {sub === "accounts"   && <AirqAccounts/>}
    </div>
  );
}

function AirqOverview() {
  const [pads, setPads] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [missions, setMissions] = useState([]);
  const [showPad, setShowPad] = useState(false);
  const [showAc,  setShowAc]  = useState(false);
  const [showMx,  setShowMx]  = useState(false);
  const [logMid,  setLogMid]  = useState(null);
  const [escMid,  setEscMid]  = useState(null);
  const [showBulk, setShowBulk] = useState(false);

  const load = async () => {
    try {
      const [p, a, m] = await Promise.all([
        api.get("/wave4/airq/pads"),
        api.get("/wave4/airq/aircraft"),
        api.get("/wave4/airq/missions?days=30"),
      ]);
      setPads(p.data.pads || []);
      setAircraft(a.data.aircraft || []);
      setMissions(m.data.missions || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="airq-tab">
      <div className="p-4 rounded-lg bg-[#E0F2FE] border border-[#7DD3FC] text-sm text-[#0C4A6E]">
        <b>AirQ · Air Ambulance + Drones</b> · Pad + aircraft registry, aeromedical
        missions layered on top of ground ambulance, DGCA-style append-only flight log
        with mayday auto-escalation.
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Pads"      v={pads.length}     c="#0369A1"/>
        <StatTile label="Aircraft"  v={aircraft.length} c="#0F766E"/>
        <StatTile label="In flight" v={aircraft.filter(a => a.status==="in_flight").length} c="#B91C1C"/>
        <StatTile label="Missions"  v={missions.length} c="#7C3AED"/>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-[#8A8780]">Pads</div>
            <div className="flex items-center gap-1">
              <button onClick={() => setShowBulk(true)} data-testid="airq-pad-bulk"
                className="flex items-center gap-1 text-xs px-2 py-1 bg-[#7C2D12] text-white rounded">
                <Plus size={12}/>CSV
              </button>
              <button onClick={() => setShowPad(true)} data-testid="airq-pad-add"
                className="flex items-center gap-1 text-xs px-2 py-1 bg-[#0369A1] text-white rounded">
                <Plus size={12}/>Pad
              </button>
            </div>
          </div>
          {pads.length === 0 ? <div className="text-sm text-[#8A8780]">No pads</div>
            : pads.slice(0,12).map(p => (
              <div key={p.id} className="flex items-center justify-between py-1.5 border-b last:border-0 text-sm">
                <div className="flex items-center gap-2">
                  <MapPin size={14} className="text-[#0369A1]"/>
                  <span>{p.name}</span>
                </div>
                <span className="text-[11px] text-[#8A8780]">{p.kind} · {p.lat.toFixed(2)},{p.lng.toFixed(2)}</span>
              </div>
            ))}
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-[#8A8780]">Aircraft</div>
            <button onClick={() => setShowAc(true)} data-testid="airq-aircraft-add"
              className="flex items-center gap-1 text-xs px-2 py-1 bg-[#0F766E] text-white rounded">
              <Plus size={12}/>Aircraft
            </button>
          </div>
          {aircraft.length === 0 ? <div className="text-sm text-[#8A8780]">No aircraft</div>
            : aircraft.slice(0,12).map(a => (
              <div key={a.id} className="flex items-center justify-between py-1.5 border-b last:border-0 text-sm">
                <div className="flex items-center gap-2">
                  <Airplane size={14} className="text-[#0F766E]"/>
                  <span className="font-mono">{a.call_sign}</span>
                  <span className="text-[11px] text-[#8A8780]">{a.kind}</span>
                </div>
                <StatusPill status={a.status}/>
              </div>
            ))}
        </div>
      </div>

      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-[#8A8780]">Aeromedical missions (30d)</div>
          <button onClick={() => setShowMx(true)} data-testid="airq-mission-add"
            className="flex items-center gap-1 text-xs px-2 py-1 bg-[#7C3AED] text-white rounded">
            <PlayCircle size={12}/>New Mission
          </button>
        </div>
        {missions.length === 0 ? <div className="text-sm text-[#8A8780]">No missions yet</div>
          : missions.slice(0,12).map(m => (
            <div key={m.id} className="flex items-center justify-between py-2 border-b last:border-0 text-sm"
                 data-testid={`airq-mission-${m.id.slice(0,6)}`}>
              <div>
                <div className="font-medium">{m.mission_no} · {m.mission_kind}</div>
                <div className="text-[11px] text-[#8A8780]">
                  {m.distance_km} km · {m.aircraft_call_sign || "unassigned"} · P{m.priority?.replace("P","")}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusPill status={m.status}/>
                <button onClick={() => setLogMid(m.id)}
                  data-testid={`airq-log-${m.id.slice(0,6)}`}
                  className="p-1 rounded bg-[#4338CA] text-white text-[11px] px-2">Log</button>
                <button onClick={() => setEscMid(m.id)}
                  data-testid={`airq-esc-${m.id.slice(0,6)}`}
                  className="p-1 rounded bg-[#B91C1C] text-white text-[11px] px-2">Escalate</button>
              </div>
            </div>
          ))}
      </div>

      {showPad && <PadModal onClose={() => setShowPad(false)} onSaved={() => { setShowPad(false); load(); }}/>}
      {showAc  && <AircraftModal pads={pads} onClose={() => setShowAc(false)} onSaved={() => { setShowAc(false); load(); }}/>}
      {showMx  && <MissionModal aircraft={aircraft} onClose={() => setShowMx(false)} onSaved={() => { setShowMx(false); load(); }}/>}
      {logMid  && <FlightLogModal mid={logMid} onClose={() => setLogMid(null)} onSaved={() => { setLogMid(null); load(); }}/>}
      {escMid  && <EscalationModal mid={escMid} onClose={() => setEscMid(null)}/>}
      {showBulk && <BulkImportModal onClose={() => setShowBulk(false)}
                                              onDone={() => { setShowBulk(false); load(); }}/>}
    </div>
  );
}

function PadModal({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", kind: "helipad", lat: "", lng: "", night_ops: false });
  const submit = async () => {
    try {
      await api.post("/wave4/airq/pads", { ...f, lat: parseFloat(f.lat), lng: parseFloat(f.lng) });
      toast.success("Pad created"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Modal onClose={onClose} title="Add pad">
      <Input placeholder="Pad name" value={f.name} onChange={v => setF({...f, name:v})} testid="pad-name"/>
      <Select value={f.kind} onChange={v => setF({...f, kind:v})} testid="pad-kind"
              options={[["helipad","Helipad"],["dronepad","Dronepad"],["hospital_helipad","Hospital helipad"]]}/>
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Latitude" value={f.lat} onChange={v => setF({...f, lat:v})} testid="pad-lat"/>
        <Input placeholder="Longitude" value={f.lng} onChange={v => setF({...f, lng:v})} testid="pad-lng"/>
      </div>
      <label className="flex items-center gap-2 text-sm mb-3">
        <input type="checkbox" checked={f.night_ops} onChange={e => setF({...f, night_ops:e.target.checked})}/>
        Night operations
      </label>
      <button onClick={submit} data-testid="pad-save"
              className="w-full py-2 rounded bg-[#0369A1] text-white">Create pad</button>
    </Modal>
  );
}

function AircraftModal({ pads, onClose, onSaved }) {
  const [f, setF] = useState({ call_sign: "", kind: "helicopter", make_model: "",
                                     range_km: 300, home_pad_id: "" });
  const submit = async () => {
    try {
      await api.post("/wave4/airq/aircraft", { ...f, range_km: parseFloat(f.range_km) });
      toast.success("Aircraft added"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Modal onClose={onClose} title="Add aircraft">
      <Input placeholder="Call sign (VT-XXX)" value={f.call_sign} onChange={v => setF({...f, call_sign:v.toUpperCase()})} testid="ac-callsign"/>
      <Select value={f.kind} onChange={v => setF({...f, kind:v})} testid="ac-kind"
              options={[["helicopter","Helicopter"],["fixed_wing","Fixed wing"],["drone","Drone"]]}/>
      <Input placeholder="Make / model" value={f.make_model} onChange={v => setF({...f, make_model:v})} testid="ac-model"/>
      <Input placeholder="Range (km)" value={f.range_km} onChange={v => setF({...f, range_km:v})} testid="ac-range"/>
      <Select value={f.home_pad_id} onChange={v => setF({...f, home_pad_id:v})} testid="ac-pad"
              options={[["","— pick home pad —"], ...pads.map(p => [p.id, p.name])]}/>
      <button onClick={submit} data-testid="ac-save"
              className="w-full py-2 rounded bg-[#0F766E] text-white">Add aircraft</button>
    </Modal>
  );
}

function MissionModal({ aircraft, onClose, onSaved }) {
  const [f, setF] = useState({
    mission_kind: "patient_evac",
    origin_lat: "", origin_lng: "",
    dest_lat: "",   dest_lng: "",
    priority: "P1", notes: "",
  });
  const [candidates, setCandidates] = useState([]);
  const [mid, setMid] = useState(null);

  const create = async () => {
    try {
      const r = await api.post("/wave4/airq/missions", {
        ...f,
        origin_lat: parseFloat(f.origin_lat), origin_lng: parseFloat(f.origin_lng),
        dest_lat:   parseFloat(f.dest_lat),   dest_lng:   parseFloat(f.dest_lng),
      });
      setMid(r.data.id);
      const c = await api.post(`/wave4/airq/missions/${r.data.id}/candidates`);
      setCandidates(c.data.candidates || []);
      toast.success(`Mission ${r.data.mission_no} created`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const dispatch = async (ac_id) => {
    try {
      await api.post(`/wave4/airq/missions/${mid}/dispatch`, { aircraft_id: ac_id });
      toast.success("Dispatched"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Modal onClose={onClose} title={mid ? "Dispatch aircraft" : "New aeromedical mission"}>
      {!mid ? (
        <>
          <Select value={f.mission_kind} onChange={v => setF({...f, mission_kind:v})} testid="mx-kind"
                  options={[["patient_evac","Patient evac"],["organ_transfer","Organ transfer"],
                              ["blood_delivery","Blood delivery"],["medicine_drop","Medicine drop"],
                              ["specialist_shuttle","Specialist shuttle"]]}/>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Origin lat" value={f.origin_lat} onChange={v => setF({...f, origin_lat:v})} testid="mx-olat"/>
            <Input placeholder="Origin lng" value={f.origin_lng} onChange={v => setF({...f, origin_lng:v})} testid="mx-olng"/>
            <Input placeholder="Dest lat"   value={f.dest_lat}   onChange={v => setF({...f, dest_lat:v})} testid="mx-dlat"/>
            <Input placeholder="Dest lng"   value={f.dest_lng}   onChange={v => setF({...f, dest_lng:v})} testid="mx-dlng"/>
          </div>
          <Select value={f.priority} onChange={v => setF({...f, priority:v})} testid="mx-pri"
                  options={[["P0","P0 · Life-threat"],["P1","P1 · Urgent"],["P2","P2 · Scheduled"]]}/>
          <button onClick={create} data-testid="mx-save"
                   className="w-full py-2 rounded bg-[#7C3AED] text-white">Create + rank aircraft</button>
        </>
      ) : (
        <>
          <div className="text-xs text-[#5C5A55] mb-2">Ranked by proximity + range</div>
          {candidates.length === 0
            ? <div className="text-sm text-[#8A8780] py-4">No aircraft available in range.</div>
            : candidates.map(c => (
              <div key={c.id} className="flex items-center justify-between py-2 border-b last:border-0">
                <div>
                  <div className="text-sm font-medium font-mono">{c.call_sign}</div>
                  <div className="text-[11px] text-[#8A8780]">
                    {c.kind} · {c.km_to_origin != null ? `${c.km_to_origin} km` : "range unknown"}
                    {c.eta_min && ` · ETA ${c.eta_min}m`}
                  </div>
                </div>
                <button onClick={() => dispatch(c.id)}
                          data-testid={`mx-dispatch-${c.call_sign}`}
                          className="px-3 py-1 bg-[#B91C1C] text-white rounded text-sm">
                  Dispatch
                </button>
              </div>
            ))}
        </>
      )}
    </Modal>
  );
}

function FlightLogModal({ mid, onClose, onSaved }) {
  const [event, setEvent] = useState("waypoint");
  const [note, setNote] = useState("");
  const [alt, setAlt] = useState("");
  const submit = async () => {
    try {
      await api.post(`/wave4/airq/missions/${mid}/log`, {
        event, note, altitude_m: alt ? parseFloat(alt) : null,
      });
      toast.success("Log entry saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const complete = async () => {
    try {
      await api.post(`/wave4/airq/missions/${mid}/complete?note=${encodeURIComponent(note)}`);
      toast.success("Mission completed"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Modal onClose={onClose} title="Flight log entry">
      <Select value={event} onChange={setEvent} testid="log-event"
              options={[["takeoff","Takeoff"],["waypoint","Waypoint"],["landing","Landing"],
                          ["abort","Abort"],["mayday","MAYDAY"],["note","Note"]]}/>
      <Input placeholder="Altitude (m)" value={alt} onChange={setAlt} testid="log-alt"/>
      <Input placeholder="Note" value={note} onChange={setNote} testid="log-note"/>
      <div className="grid grid-cols-2 gap-2">
        <button onClick={submit} data-testid="log-save"
                className="py-2 rounded bg-[#4338CA] text-white text-sm">Append entry</button>
        <button onClick={complete} data-testid="log-complete"
                className="py-2 rounded bg-[#059669] text-white text-sm">Complete mission</button>
      </div>
    </Modal>
  );
}

// ---- Iter 230 · AirQ Tranche 1 sub-tabs ---------------------------

function EscalationModal({ mid, onClose }) {
  const [reason, setReason] = useState("weather");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/wave4/airq/missions/${mid}/escalate`, { reason });
      setResult(r.data);
      toast.success("Escalation ranked");
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  return (
    <Modal onClose={onClose} title="Escalate mission">
      <Select value={reason} onChange={setReason} testid="esc-reason"
              options={[["weather","Weather block"],["aircraft_fault","Aircraft fault"],
                          ["hospital_full","Hospital full"],["atc_denied","ATC denied"],
                          ["pilot_unavailable","Pilot unavailable"],["other","Other"]]}/>
      <button onClick={run} disabled={loading} data-testid="esc-run"
              className="w-full py-2 rounded bg-[#B91C1C] text-white disabled:opacity-50">
        {loading ? "Ranking…" : "Run rules engine"}
      </button>
      {result && (
        <div className="mt-3 space-y-2 text-sm">
          <PropSection title="Alt aircraft" items={result.proposed.alt_aircraft}
                        render={a => `${a.call_sign} · ${a.km_to_origin ?? "?"} km`}/>
          <PropSection title="Alt pads" items={result.proposed.alt_pads}
                        render={p => `${p.name} · ${p.km_to_dest} km`}/>
          {reason === "hospital_full" && (
            <PropSection title="Alt hospitals" items={result.proposed.alt_hospitals}
                          render={h => `${h.name || h.id.slice(0,8)} · ${h.km_to_dest} km`}/>
          )}
        </div>
      )}
    </Modal>
  );
}
function PropSection({ title, items, render }) {
  if (!items || !items.length) return <div className="text-xs text-[#8A8780]">{title}: none</div>;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[#8A8780] mb-1">{title}</div>
      {items.map((it, i) => (
        <div key={i} className="text-xs py-0.5 border-b last:border-0">{render(it)}</div>
      ))}
    </div>
  );
}

// ----- Bulk CSV import modal (Iter 233) ----------------------------
const CSV_TEMPLATE = "name,kind,lat,lng,state,district,lsgd,ward,ownership,surface,night_ops,licence_no,helipad_id,govt_id,csr_public_use\n" +
                     "Kalamassery hospital pad,hospital_helipad,10.0554,76.3120,Kerala,Ernakulam,Kalamassery,Ward-12,hospital,concrete,true,,,,false\n" +
                     "Kakkanad drone pad,dronepad,10.0158,76.3419,Kerala,Ernakulam,Thrikkakara,,builder,asphalt,false,,,,true\n";

function BulkImportModal({ onClose, onDone }) {
  const [csv, setCsv] = useState(CSV_TEMPLATE);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCsv(await f.text());
    setPreview(null);
  };
  const run = async (dry_run) => {
    setBusy(true);
    try {
      const r = await api.post("/wave4/airq/pads/bulk-import",
                                     { csv_text: csv, dry_run });
      setPreview(r.data);
      if (!dry_run) {
        toast.success(`Imported ${r.data.inserted} pads · ${r.data.skipped} skipped`);
        onDone();
      } else {
        toast.success(`Dry-run · ${r.data.would_insert} would import`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full p-5 max-h-[92vh] overflow-y-auto"
             onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg">Bulk import pads · CSV</h2>
          <button onClick={onClose} className="text-2xl leading-none">×</button>
        </div>
        <div className="text-[11px] text-[#5C5A55] mb-2">
          Required columns: <code>name,kind,lat,lng</code>. Optional:
          <code> state, district, lsgd, ward, ownership, surface, night_ops,
          licence_no, helipad_id, govt_id, csr_public_use</code>. State /
          district / LSGD / ward are matched case-insensitively by name.
        </div>
        <input type="file" accept=".csv,text/csv"
                 onChange={onFile} data-testid="bulk-file"
                 className="mb-2 text-sm"/>
        <textarea value={csv} onChange={e => setCsv(e.target.value)}
                     data-testid="bulk-csv"
                     className="w-full h-56 border rounded p-2 text-xs font-mono"/>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={() => run(true)} disabled={busy}
                    data-testid="bulk-dry"
                    className="px-3 py-2 rounded bg-[#4338CA] text-white text-sm disabled:opacity-50">
            {busy ? "…" : "Dry-run"}
          </button>
          <button onClick={() => run(false)} disabled={busy}
                    data-testid="bulk-run"
                    className="px-3 py-2 rounded bg-[#7C2D12] text-white text-sm disabled:opacity-50">
            {busy ? "…" : "Import"}
          </button>
        </div>
        {preview && (
          <div className="mt-3 text-xs space-y-1">
            <div>Inserted: <b>{preview.inserted}</b>{preview.would_insert != null && ` · would insert ${preview.would_insert}`} · Skipped: <b>{preview.skipped}</b></div>
            {preview.warnings?.length > 0 && (
              <details className="border rounded p-2 bg-[#FEF3C7]">
                <summary className="cursor-pointer">Warnings ({preview.warnings.length})</summary>
                {preview.warnings.map((w, i) => (
                  <div key={i}>Row {w.row}: {w.warnings.join(", ")}</div>
                ))}
              </details>
            )}
            {preview.errors?.length > 0 && (
              <details className="border rounded p-2 bg-[#FEE2E2]">
                <summary className="cursor-pointer">Errors ({preview.errors.length})</summary>
                {preview.errors.map((e, i) => (
                  <div key={i}>Row {e.row}: {e.reason}</div>
                ))}
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ----- Cases sub-tab -----------------------------------------------
function AirqCases() {
  const [cases, setCases] = useState([]);
  const [filter, setFilter] = useState("");
  const [editMid, setEditMid] = useState(null);
  const load = async () => {
    try {
      const q = filter ? `?status=${filter}` : "";
      const r = await api.get(`/wave4/airq/cases${q}`);
      setCases(r.data.cases || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [filter]);   // eslint-disable-line
  const editCase = cases.find(c => c.id === editMid);
  return (
    <div className="space-y-3" data-testid="airq-cases">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#5C5A55]">{cases.length} cases</div>
        <Select value={filter} onChange={setFilter} testid="cases-filter"
                options={[["","All statuses"],["intake","Intake"],["triaged","Triaged"],
                            ["in_flight","In flight"],["handed_over","Handed over"],
                            ["closed","Closed"],["cancelled","Cancelled"]]}/>
      </div>
      <div className="bg-white rounded-lg border p-4">
        {cases.length === 0 ? <div className="text-sm text-[#8A8780]">No cases</div>
          : cases.slice(0,50).map(c => (
            <div key={c.id} className="flex items-center justify-between py-2 border-b last:border-0 text-sm"
                 data-testid={`case-row-${c.id.slice(0,6)}`}>
              <div>
                <div className="font-medium">{c.mission_no}</div>
                <div className="text-[11px] text-[#8A8780]">
                  {c.patient_name || "unnamed"} · {c.mission_kind}
                  {c.receiving_hospital_name && ` → ${c.receiving_hospital_name}`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusPill status={c.case_status || c.status}/>
                <button onClick={() => setEditMid(c.id)}
                          data-testid={`case-edit-${c.id.slice(0,6)}`}
                          className="text-[11px] px-2 py-1 rounded bg-[#4338CA] text-white">
                  Edit
                </button>
              </div>
            </div>
          ))}
      </div>
      {editCase && <CaseEditModal mission={editCase} onClose={() => setEditMid(null)}
                                          onSaved={() => { setEditMid(null); load(); }}/>}
    </div>
  );
}
function CaseEditModal({ mission, onClose, onSaved }) {
  const [f, setF] = useState({
    case_status: mission.case_status || "intake",
    patient_name: mission.patient_name || "",
    patient_phone: mission.patient_phone || "",
    receiving_hospital_name: mission.receiving_hospital_name || "",
    diagnosis_summary: mission.diagnosis_summary || "",
    insurance_ref: mission.insurance_ref || "",
    case_note: "",
  });
  const submit = async () => {
    try {
      await api.patch(`/wave4/airq/missions/${mission.id}/case`, f);
      toast.success("Case updated"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Modal onClose={onClose} title={`Case ${mission.mission_no}`}>
      <Select value={f.case_status} onChange={v => setF({...f, case_status:v})} testid="case-status"
              options={[["intake","Intake"],["triaged","Triaged"],["in_flight","In flight"],
                          ["handed_over","Handed over"],["closed","Closed"],["cancelled","Cancelled"]]}/>
      <Input placeholder="Patient name" value={f.patient_name} onChange={v => setF({...f, patient_name:v})} testid="case-pname"/>
      <Input placeholder="Patient phone" value={f.patient_phone} onChange={v => setF({...f, patient_phone:v})} testid="case-pphone"/>
      <Input placeholder="Receiving hospital" value={f.receiving_hospital_name} onChange={v => setF({...f, receiving_hospital_name:v})} testid="case-hosp"/>
      <Input placeholder="Diagnosis summary" value={f.diagnosis_summary} onChange={v => setF({...f, diagnosis_summary:v})} testid="case-diag"/>
      <Input placeholder="Insurance ref" value={f.insurance_ref} onChange={v => setF({...f, insurance_ref:v})} testid="case-ins"/>
      <Input placeholder="Case note" value={f.case_note} onChange={v => setF({...f, case_note:v})} testid="case-note"/>
      <button onClick={submit} data-testid="case-save"
              className="w-full py-2 rounded bg-[#4338CA] text-white">Save case</button>
    </Modal>
  );
}

// ----- Crew sub-tab ------------------------------------------------
function AirqCrew() {
  const [crew, setCrew] = useState([]);
  const [role, setRole] = useState("");
  const [roles, setRoles] = useState([]);
  const load = async () => {
    try {
      const q = role ? `?role=${role}` : "";
      const r = await api.get(`/wave4/airq/crew${q}`);
      setCrew(r.data.crew || []);
      setRoles(r.data.roles || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [role]);   // eslint-disable-line
  return (
    <div className="space-y-3" data-testid="airq-crew">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#5C5A55]">
          {crew.length} aviation staff · derived from office_staff (roles: {roles.join(", ")})
        </div>
        <Select value={role} onChange={setRole} testid="crew-role"
                options={[["","All aviation roles"], ...roles.map(r => [r, r.replace("_"," ")])]}/>
      </div>
      <div className="bg-white rounded-lg border p-4">
        {crew.length === 0 ? (
          <div className="text-sm text-[#8A8780]">
            No aviation crew yet. Flag office_staff records with role=pilot / paramedic / flight_nurse / aviation_engineer.
          </div>
        ) : crew.map(c => (
          <div key={c.id} className="flex items-center justify-between py-2 border-b last:border-0 text-sm">
            <div>
              <div className="font-medium">{c.name}</div>
              <div className="text-[11px] text-[#8A8780]">{c.phone} · {c.role}</div>
            </div>
            <span className="text-[11px] uppercase tracking-wider text-[#0F766E]">{c.role}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ----- Compliance sub-tab ------------------------------------------
function AirqCompliance() {
  const [docs, setDocs] = useState([]);
  const [expWithin, setExpWithin] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const load = async () => {
    try {
      const q = expWithin ? `?expiring_within_days=${expWithin}` : "";
      const r = await api.get(`/wave4/airq/compliance/docs${q}`);
      setDocs(r.data.docs || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [expWithin]);   // eslint-disable-line
  const archive = async (id) => {
    try {
      await api.delete(`/wave4/airq/compliance/docs/${id}`);
      toast.success("Archived"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-3" data-testid="airq-compliance">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#5C5A55]">{docs.length} active docs</div>
        <div className="flex items-center gap-2">
          <Select value={expWithin} onChange={setExpWithin} testid="comp-filter"
                  options={[["","All active"],["30","Expiring in 30d"],
                              ["7","Expiring in 7d"],["0","Already expired"]]}/>
          <button onClick={() => setShowAdd(true)} data-testid="comp-add"
                    className="flex items-center gap-1 text-xs px-3 py-1.5 bg-[#B45309] text-white rounded">
            <Plus size={12}/>Doc
          </button>
        </div>
      </div>
      <div className="bg-white rounded-lg border p-4">
        {docs.length === 0 ? <div className="text-sm text-[#8A8780]">No compliance docs</div>
          : docs.slice(0,50).map(d => (
            <div key={d.id} className="flex items-center justify-between py-2 border-b last:border-0 text-sm"
                 data-testid={`comp-row-${d.id.slice(0,6)}`}>
              <div>
                <div className="font-medium">{d.doc_kind.replace("_"," ")} · {d.doc_ref || "no ref"}</div>
                <div className="text-[11px] text-[#8A8780]">
                  {d.subject_kind} {d.subject_id.slice(0,8)} · expires {d.expires_on || "—"}
                </div>
              </div>
              <button onClick={() => archive(d.id)}
                        data-testid={`comp-archive-${d.id.slice(0,6)}`}
                        className="text-[11px] px-2 py-1 rounded bg-[#7F1D1D] text-white">
                Archive
              </button>
            </div>
          ))}
      </div>
      {showAdd && <ComplianceModal onClose={() => setShowAdd(false)}
                                             onSaved={() => { setShowAdd(false); load(); }}/>}
    </div>
  );
}
function ComplianceModal({ onClose, onSaved }) {
  const [f, setF] = useState({ doc_kind: "dgca_permit", subject_kind: "aircraft",
                                     subject_id: "", doc_ref: "", doc_url: "",
                                     issued_on: "", expires_on: "" });
  const submit = async () => {
    try {
      await api.post("/wave4/airq/compliance/docs", f);
      toast.success("Doc added"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Modal onClose={onClose} title="Add compliance doc">
      <Select value={f.doc_kind} onChange={v => setF({...f, doc_kind:v})} testid="comp-kind"
              options={[["dgca_permit","DGCA permit"],["airworthiness_cert","Airworthiness cert"],
                          ["pilot_licence","Pilot licence"],["route_clearance","Route clearance"],
                          ["insurance_cert","Insurance cert"],["atc_notam","ATC NOTAM"],
                          ["other","Other"]]}/>
      <Select value={f.subject_kind} onChange={v => setF({...f, subject_kind:v})} testid="comp-subj-kind"
              options={[["aircraft","Aircraft"],["pad","Pad"],["crew","Crew"],
                          ["mission","Mission"],["operator","Operator"]]}/>
      <Input placeholder="Subject ID" value={f.subject_id} onChange={v => setF({...f, subject_id:v})} testid="comp-subj-id"/>
      <Input placeholder="Doc reference no." value={f.doc_ref} onChange={v => setF({...f, doc_ref:v})} testid="comp-ref"/>
      <Input placeholder="Doc URL (optional)" value={f.doc_url} onChange={v => setF({...f, doc_url:v})} testid="comp-url"/>
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Issued (YYYY-MM-DD)" value={f.issued_on} onChange={v => setF({...f, issued_on:v})} testid="comp-issued"/>
        <Input placeholder="Expires (YYYY-MM-DD)" value={f.expires_on} onChange={v => setF({...f, expires_on:v})} testid="comp-exp"/>
      </div>
      <button onClick={submit} data-testid="comp-save"
              className="w-full py-2 rounded bg-[#B45309] text-white">Add doc</button>
    </Modal>
  );
}

// ----- Approvals sub-tab -------------------------------------------
function AirqApprovals() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("submitted");
  const [showAdd, setShowAdd] = useState(false);
  const load = async () => {
    try {
      const q = status ? `?status=${status}` : "";
      const r = await api.get(`/wave4/airq/pad-approvals${q}`);
      setRows(r.data.approvals || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [status]);   // eslint-disable-line
  const act = async (aid, action) => {
    try {
      await api.post(`/wave4/airq/pad-approvals/${aid}/action`, { action });
      toast.success(`Approval ${action}`); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-3" data-testid="airq-approvals">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#5C5A55]">{rows.length} approvals</div>
        <div className="flex items-center gap-2">
          <Select value={status} onChange={setStatus} testid="appr-filter"
                  options={[["","All"],["submitted","Submitted"],["approved","Approved"],
                              ["rejected","Rejected"],["info_requested","Info requested"]]}/>
          <button onClick={() => setShowAdd(true)} data-testid="appr-add"
                    className="flex items-center gap-1 text-xs px-3 py-1.5 bg-[#0369A1] text-white rounded">
            <Plus size={12}/>Submit approval
          </button>
        </div>
      </div>
      <div className="bg-white rounded-lg border p-4">
        {rows.length === 0 ? <div className="text-sm text-[#8A8780]">No approvals</div>
          : rows.slice(0,50).map(a => (
            <div key={a.id} className="flex items-center justify-between py-2 border-b last:border-0 text-sm"
                 data-testid={`appr-row-${a.id.slice(0,6)}`}>
              <div>
                <div className="font-medium">{a.approval_no} · {a.proposed_pad?.name}</div>
                <div className="text-[11px] text-[#8A8780]">
                  {a.proposed_pad?.kind} · {a.proposed_pad?.ownership || "?"}
                  {a.proposed_pad?.helipad_id && ` · ${a.proposed_pad.helipad_id}`}
                  {a.proposed_pad?.govt_id && ` · ${a.proposed_pad.govt_id}`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusPill status={a.status}/>
                {a.status === "submitted" && (
                  <>
                    <button onClick={() => act(a.id, "approve")}
                              data-testid={`appr-approve-${a.id.slice(0,6)}`}
                              className="text-[11px] px-2 py-1 rounded bg-[#166534] text-white">
                      Approve
                    </button>
                    <button onClick={() => act(a.id, "reject")}
                              data-testid={`appr-reject-${a.id.slice(0,6)}`}
                              className="text-[11px] px-2 py-1 rounded bg-[#7F1D1D] text-white">
                      Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
      </div>
      {showAdd && <PadApprovalModal onClose={() => setShowAdd(false)}
                                              onSaved={() => { setShowAdd(false); load(); }}/>}
    </div>
  );
}
function PadApprovalModal({ onClose, onSaved }) {
  const [pad, setPad] = useState({
    name: "", kind: "hospital_helipad", lat: "", lng: "",
    ownership: "builder",
    state_id: "", district_id: "", lsgd_id: "", ward_id: "",
    licence_no: "",
  });
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [lsgds, setLsgds] = useState([]);
  const [wards, setWards] = useState([]);

  // Cascade: load states once, then children on parent change
  useEffect(() => {
    api.get("/wave4/airq/locations?type=state")
      .then(r => setStates(r.data.locations || []))
      .catch(() => {});
  }, []);
  useEffect(() => {
    setDistricts([]); setLsgds([]); setWards([]);
    setPad(p => ({ ...p, district_id: "", lsgd_id: "", ward_id: "" }));
    if (!pad.state_id) return;
    api.get(`/wave4/airq/locations?type=district&parent_id=${pad.state_id}`)
      .then(r => setDistricts(r.data.locations || []));
  }, [pad.state_id]);   // eslint-disable-line
  useEffect(() => {
    setLsgds([]); setWards([]);
    setPad(p => ({ ...p, lsgd_id: "", ward_id: "" }));
    if (!pad.district_id) return;
    api.get(`/wave4/airq/locations?type=lsgd&parent_id=${pad.district_id}`)
      .then(r => setLsgds(r.data.locations || []));
  }, [pad.district_id]);   // eslint-disable-line
  useEffect(() => {
    setWards([]);
    setPad(p => ({ ...p, ward_id: "" }));
    if (!pad.lsgd_id) return;
    api.get(`/wave4/airq/locations?type=ward&parent_id=${pad.lsgd_id}`)
      .then(r => setWards(r.data.locations || []));
  }, [pad.lsgd_id]);   // eslint-disable-line

  // Cache resolved display names for the payload (helps legacy consumers)
  const nameOf = (arr, id) => (arr.find(x => x.id === id) || {}).name || "";

  const submit = async () => {
    if (!pad.name || !pad.lat || !pad.lng) return toast.error("Name + coords required");
    try {
      await api.post("/wave4/airq/pad-approvals", {
        proposed_pad: {
          ...pad,
          lat: parseFloat(pad.lat), lng: parseFloat(pad.lng),
          district: nameOf(districts, pad.district_id) || undefined,
          lsgd:     nameOf(lsgds,     pad.lsgd_id)     || undefined,
          ward:     nameOf(wards,     pad.ward_id)     || undefined,
        }});
      toast.success("Submitted · helipad_id + govt_id auto-generated"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Modal onClose={onClose} title="Submit pad for approval">
      <div className="mb-3 p-2 bg-[#EEF7FF] border border-[#93C5FD] rounded text-[11px] text-[#1E3A8A]">
        On submit, <b>helipad_id</b> and <b>govt_id</b> are auto-generated
        from state + district + a random suffix.
      </div>
      <Input placeholder="Pad name" value={pad.name} onChange={v => setPad({...pad, name:v})} testid="appr-name"/>
      <Select value={pad.kind} onChange={v => setPad({...pad, kind:v})} testid="appr-kind"
              options={[["helipad","Helipad"],["dronepad","Dronepad"],
                          ["hospital_helipad","Hospital helipad"],["hybrid","Hybrid"],
                          ["runway","Runway"],["airport","Airport"]]}/>
      <Select value={pad.ownership} onChange={v => setPad({...pad, ownership:v})} testid="appr-own"
              options={[["builder","Builder"],["hospital","Hospital"],
                          ["public","Public"],["private","Private"],["govt","Govt"]]}/>
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Latitude" value={pad.lat} onChange={v => setPad({...pad, lat:v})} testid="appr-lat"/>
        <Input placeholder="Longitude" value={pad.lng} onChange={v => setPad({...pad, lng:v})} testid="appr-lng"/>
      </div>

      {/* Jurisdiction dropdowns */}
      <div className="text-[11px] uppercase tracking-wider text-[#8A8780] mt-2 mb-1">Jurisdiction</div>
      <Select value={pad.state_id} onChange={v => setPad({...pad, state_id:v})} testid="appr-state"
              options={[["","— select state —"],
                          ...states.map(s => [s.id, s.name])]}/>
      <Select value={pad.district_id} onChange={v => setPad({...pad, district_id:v})} testid="appr-district"
              options={[["", pad.state_id ? "— select district —" : "(pick state first)"],
                          ...districts.map(d => [d.id, d.name])]}/>
      <Select value={pad.lsgd_id} onChange={v => setPad({...pad, lsgd_id:v})} testid="appr-lsgd"
              options={[["", pad.district_id ? "— select LSGD —" : "(pick district first)"],
                          ...lsgds.map(l => [l.id, l.name])]}/>
      <Select value={pad.ward_id} onChange={v => setPad({...pad, ward_id:v})} testid="appr-ward"
              options={[["", pad.lsgd_id ? "— select ward —" : "(pick LSGD first)"],
                          ...wards.map(w => [w.id, w.name])]}/>
      <Input placeholder="Licence no (optional)" value={pad.licence_no} onChange={v => setPad({...pad, licence_no:v})} testid="appr-lic"/>

      <button onClick={submit} data-testid="appr-save"
              className="w-full py-2 rounded bg-[#0369A1] text-white">Submit</button>
    </Modal>
  );
}


// ----- Accounts sub-tab (pad_operator + aeromed_admin logins) ------
function AirqAccounts() {
  const [subtype, setSubtype] = useState("");
  const [rows, setRows] = useState([]);
  const [pads, setPads] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const load = async () => {
    try {
      const q = subtype ? `?subtype=${subtype}` : "";
      const [a, p, ac] = await Promise.all([
        api.get(`/wave4/airq/accounts${q}`),
        api.get("/wave4/airq/pads"),
        api.get("/wave4/airq/aircraft"),
      ]);
      setRows(a.data.accounts || []);
      setPads(p.data.pads || []);
      setAircraft(ac.data.aircraft || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [subtype]);   // eslint-disable-line
  const toggleActive = async (acc) => {
    try {
      await api.patch(`/wave4/airq/accounts/${acc.id}`,
                        { is_active: !acc.is_active });
      toast.success(acc.is_active ? "Deactivated" : "Activated"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const resetPw = async (acc) => {
    const p = window.prompt(`New password for ${acc.email} (min 6):`);
    if (!p || p.length < 6) return toast.error("Password too short");
    try {
      await api.patch(`/wave4/airq/accounts/${acc.id}`, { password: p });
      toast.success("Password reset");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const resendWelcome = async (acc) => {
    if (!window.confirm(`Rotate password and re-email ${acc.email}?`)) return;
    try {
      const r = await api.post(`/wave4/airq/accounts/${acc.id}/resend-welcome`);
      toast.success(`Welcome resent (email ${r.data.email_status})`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const impersonate = async (acc) => {
    if (!window.confirm(
      `Sign in as ${acc.email}? A 30-min session will open in a new tab.`
    )) return;
    try {
      const r = await api.post(`/wave4/airq/accounts/${acc.id}/impersonate`);
      localStorage.setItem("airq_account_token", r.data.access_token);
      toast.success("Impersonation token minted");
      window.open("/airq/dashboard", "_blank");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-3" data-testid="airq-accounts">
      <div className="p-3 rounded-lg bg-[#EEF7FF] border border-[#93C5FD] text-xs text-[#1E3A8A]">
        Two account subtypes: <b>pad_operator</b> (login scoped to one helipad)
        and <b>aeromed_admin</b> (login scoped to one or more aircraft). Both
        sign in at <code>/airq/login</code>.
      </div>
      <div className="flex items-center justify-between">
        <Select value={subtype} onChange={setSubtype} testid="acc-filter"
                options={[["","All accounts"],
                            ["pad_operator","Pad operator"],
                            ["aeromed_admin","Aeromed admin"]]}/>
        <button onClick={() => setShowAdd(true)} data-testid="acc-add"
                className="flex items-center gap-1 text-xs px-3 py-1.5 bg-[#166534] text-white rounded">
          <Plus size={12}/>New account
        </button>
      </div>
      <div className="bg-white rounded-lg border p-4">
        {rows.length === 0 ? <div className="text-sm text-[#8A8780]">No accounts</div>
          : rows.map(a => (
            <div key={a.id} className="flex items-center justify-between py-2 border-b last:border-0 text-sm"
                 data-testid={`acc-row-${a.id.slice(0,6)}`}>
              <div>
                <div className="font-medium">{a.name} · {a.email}</div>
                <div className="text-[11px] text-[#8A8780]">
                  {a.subtype}
                  {a.pad_id && ` · pad ${a.pad_id.slice(0,8)}`}
                  {a.aircraft_ids?.length ? ` · ${a.aircraft_ids.length} aircraft` : ""}
                  {" · "}
                  <span className={a.is_active ? "text-[#166534]" : "text-[#7F1D1D]"}>
                    {a.is_active ? "active" : "inactive"}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => resendWelcome(a)}
                          data-testid={`acc-resend-${a.id.slice(0,6)}`}
                          className="text-[11px] px-2 py-1 rounded bg-[#0369A1] text-white">
                  Resend
                </button>
                <button onClick={() => resetPw(a)}
                          data-testid={`acc-pwd-${a.id.slice(0,6)}`}
                          className="text-[11px] px-2 py-1 rounded bg-[#4338CA] text-white">
                  Reset pw
                </button>
                <button onClick={() => impersonate(a)}
                          data-testid={`acc-impersonate-${a.id.slice(0,6)}`}
                          className="text-[11px] px-2 py-1 rounded bg-[#7C2D12] text-white"
                          title="Superadmin only">
                  Sign in as
                </button>
                <button onClick={() => toggleActive(a)}
                          data-testid={`acc-toggle-${a.id.slice(0,6)}`}
                          className={`text-[11px] px-2 py-1 rounded text-white ${
                            a.is_active ? "bg-[#7F1D1D]" : "bg-[#166534]"}`}>
                  {a.is_active ? "Deactivate" : "Activate"}
                </button>
              </div>
            </div>
          ))}
      </div>
      {showAdd && <AirqAccountModal pads={pads} aircraft={aircraft}
                                             onClose={() => setShowAdd(false)}
                                             onSaved={() => { setShowAdd(false); load(); }}/>}
    </div>
  );
}
function AirqAccountModal({ pads, aircraft, onClose, onSaved }) {
  const [f, setF] = useState({ email: "", password: "", name: "",
                                     subtype: "pad_operator", phone: "",
                                     pad_id: "", aircraft_ids: [],
                                     state_ids: [], all_states: false });
  const [states, setStates] = useState([]);
  useEffect(() => {
    api.get("/wave4/airq/locations?type=state")
      .then(r => setStates(r.data.locations || []))
      .catch(() => {});
  }, []);
  const isRegulator = f.subtype === "dgca" || f.subtype === "state_aviation";
  const submit = async () => {
    if (f.password.length < 6) return toast.error("Password must be ≥ 6 chars");
    if (f.subtype === "pad_operator" && !f.pad_id) return toast.error("Pick a pad");
    if (isRegulator && !f.all_states && f.state_ids.length === 0)
      return toast.error("Assign at least one state or check All states");
    try {
      await api.post("/wave4/airq/accounts", f);
      toast.success(isRegulator
        ? "Regulator account created · email OTP enabled"
        : "Account created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleAircraft = (id) => {
    const s = new Set(f.aircraft_ids);
    s.has(id) ? s.delete(id) : s.add(id);
    setF({ ...f, aircraft_ids: Array.from(s) });
  };
  const toggleState = (id) => {
    const s = new Set(f.state_ids);
    s.has(id) ? s.delete(id) : s.add(id);
    setF({ ...f, state_ids: Array.from(s) });
  };
  return (
    <Modal onClose={onClose} title="New AirQ account">
      <Select value={f.subtype} onChange={v => setF({...f, subtype:v})} testid="acc-subtype"
              options={[["pad_operator","Pad operator (one helipad)"],
                          ["aeromed_admin","Aeromed admin (fleet-wide)"],
                          ["dgca","DGCA · Aviation regulator"],
                          ["state_aviation","State Aviation authority"]]}/>
      <Input placeholder="Full name" value={f.name} onChange={v => setF({...f, name:v})} testid="acc-name"/>
      <Input placeholder="Email" value={f.email} onChange={v => setF({...f, email:v})} testid="acc-email"/>
      <Input placeholder="Phone (optional)" value={f.phone} onChange={v => setF({...f, phone:v})} testid="acc-phone"/>
      <Input placeholder="Temp password (≥ 6 chars)" value={f.password} onChange={v => setF({...f, password:v})} testid="acc-pw"/>
      {f.subtype === "pad_operator" && (
        <Select value={f.pad_id} onChange={v => setF({...f, pad_id:v})} testid="acc-pad"
                options={[["","— pick pad —"], ...pads.map(p => [p.id, p.name])]}/>
      )}
      {f.subtype === "aeromed_admin" && (
        <>
          <div className="text-[11px] uppercase tracking-wider text-[#8A8780] mt-2">
            Aircraft ({f.aircraft_ids.length} selected · leave blank for full fleet)
          </div>
          <div className="max-h-32 overflow-y-auto border rounded p-2 mb-2">
            {aircraft.length === 0 ? <div className="text-xs text-[#8A8780]">No aircraft yet</div>
              : aircraft.map(a => (
                <label key={a.id} className="flex items-center gap-2 text-xs py-1">
                  <input type="checkbox" checked={f.aircraft_ids.includes(a.id)}
                           onChange={() => toggleAircraft(a.id)}
                           data-testid={`acc-ac-${a.call_sign}`}/>
                  <span className="font-mono">{a.call_sign}</span>
                  <span className="text-[#8A8780]">· {a.kind}</span>
                </label>
              ))}
          </div>
        </>
      )}
      {isRegulator && (
        <>
          <div className="p-2 rounded bg-[#0F172A] text-[#E2E8F0] text-[11px] mb-2">
            <b>Email OTP</b> is enabled automatically for regulator accounts.
          </div>
          <label className="flex items-center gap-2 text-sm mb-2">
            <input type="checkbox" checked={f.all_states}
                     onChange={e => setF({...f, all_states: e.target.checked})}
                     data-testid="acc-all-states"/>
            All states (national scope)
          </label>
          {!f.all_states && (
            <StatePillPicker states={states}
                                selected={f.state_ids}
                                onToggle={toggleState}/>
          )}
        </>
      )}
      <button onClick={submit} data-testid="acc-save"
              className="w-full py-2 rounded bg-[#166534] text-white">Create account</button>
    </Modal>
  );
}


// =====================================================
// 2. Schemes
// =====================================================
function SchemesTab() {
  const [schemes, setSchemes] = useState([]);
  const [claims, setClaims] = useState([]);
  const [showS, setShowS] = useState(false);
  const [walletFor, setWalletFor] = useState(null);
  const [reconFor,  setReconFor]  = useState(null);
  const load = async () => {
    try {
      const [s, c] = await Promise.all([
        api.get("/wave4/schemes"),
        api.get("/wave4/schemes/claims?days=90"),
      ]);
      setSchemes(s.data.schemes || []);
      setClaims(c.data.claims || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="schemes-tab">
      <div className="p-4 rounded-lg bg-[#FEF3C7] border border-[#FCD34D] text-sm text-[#7A5A11]">
        <b>Schemes · Karunya / KASP / Corporate</b> · Registry of govt + corporate
        schemes, wallet top-ups, per-customer eligibility gate, claim log with
        reconciliation status.
      </div>

      <div className="flex justify-between">
        <div className="text-sm text-[#5C5A55]">{schemes.length} schemes · {claims.length} claims (90d)</div>
        <button onClick={() => setShowS(true)} data-testid="scheme-add"
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-[#B45309] text-white rounded">
          <Plus size={14}/> Scheme
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white rounded-lg border p-4">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">Schemes</div>
          {schemes.length === 0 ? <div className="text-sm text-[#8A8780]">No schemes</div>
            : schemes.map(s => (
              <div key={s.id} className="flex items-center justify-between py-2 border-b last:border-0 text-sm"
                   data-testid={`scheme-row-${s.code}`}>
                <div>
                  <div className="font-medium">{s.code} · {s.name}</div>
                  <div className="text-[11px] text-[#8A8780]">
                    {s.kind} · ceiling ₹{Number(s.coverage_ceiling||0).toLocaleString("en-IN")}
                  </div>
                </div>
                <button onClick={() => setWalletFor(s)} data-testid={`scheme-wallet-${s.code}`}
                        className="p-1.5 rounded bg-[#B45309] text-white">
                  <Coins size={12}/>
                </button>
              </div>
            ))}
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">Recent claims</div>
          {claims.length === 0 ? <div className="text-sm text-[#8A8780]">No claims yet</div>
            : claims.slice(0,12).map(c => (
              <div key={c.id} className="flex items-center justify-between py-2 border-b last:border-0 text-sm">
                <div>
                  <div className="font-medium">{c.claim_no}</div>
                  <div className="text-[11px] text-[#8A8780]">
                    {c.scheme_code} · ₹{Number(c.amount_claimed).toLocaleString("en-IN")}
                    {c.amount_settled != null && ` → ₹${c.amount_settled}`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill status={c.status}/>
                  {c.status === "submitted" && (
                    <button onClick={() => setReconFor(c)}
                            data-testid={`claim-recon-${c.claim_no}`}
                            className="text-[11px] px-2 py-1 rounded bg-[#059669] text-white">
                      Reconcile
                    </button>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>

      {showS && <SchemeModal onClose={() => setShowS(false)} onSaved={() => { setShowS(false); load(); }}/>}
      {walletFor && <WalletModal scheme={walletFor} onClose={() => setWalletFor(null)} onSaved={() => setWalletFor(null)}/>}
      {reconFor && <ReconcileModal claim={reconFor} onClose={() => setReconFor(null)} onSaved={() => { setReconFor(null); load(); }}/>}
    </div>
  );
}

function SchemeModal({ onClose, onSaved }) {
  const [f, setF] = useState({ code: "", name: "", kind: "corporate",
                                     coverage_ceiling: 100000, active: true });
  const submit = async () => {
    try {
      await api.post("/wave4/schemes", { ...f, coverage_ceiling: parseFloat(f.coverage_ceiling) });
      toast.success("Scheme created"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Modal onClose={onClose} title="New scheme">
      <Input placeholder="Code (uppercase)" value={f.code} onChange={v => setF({...f, code:v.toUpperCase()})} testid="scheme-code"/>
      <Input placeholder="Display name" value={f.name} onChange={v => setF({...f, name:v})} testid="scheme-name"/>
      <Select value={f.kind} onChange={v => setF({...f, kind:v})} testid="scheme-kind"
              options={[["govt","Govt"],["corporate","Corporate"],["insurance","Insurance"]]}/>
      <Input placeholder="Coverage ceiling (₹)" value={f.coverage_ceiling}
              onChange={v => setF({...f, coverage_ceiling:v})} testid="scheme-ceiling"/>
      <button onClick={submit} data-testid="scheme-save"
              className="w-full py-2 rounded bg-[#B45309] text-white">Create scheme</button>
    </Modal>
  );
}

function WalletModal({ scheme, onClose, onSaved }) {
  const [amount, setAmount] = useState("");
  const [balance, setBalance] = useState(null);
  useEffect(() => {
    api.get(`/wave4/schemes/${scheme.code}/wallet`).then(r => setBalance(r.data.balance));
  }, [scheme.code]);
  const topUp = async () => {
    try {
      await api.post(`/wave4/schemes/${scheme.code}/wallet/top-up`,
        { scheme_code: scheme.code, balance: parseFloat(amount), top_up_note: "manual" });
      toast.success("Top-up recorded"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Modal onClose={onClose} title={`${scheme.code} wallet`}>
      <div className="text-2xl font-heading font-semibold mb-3 text-[#B45309]">
        ₹{Number(balance || 0).toLocaleString("en-IN")}
      </div>
      <Input placeholder="Top-up amount (₹)" value={amount} onChange={setAmount} testid="wallet-amt"/>
      <button onClick={topUp} data-testid="wallet-topup"
              className="w-full py-2 rounded bg-[#B45309] text-white">Top up wallet</button>
    </Modal>
  );
}

function ReconcileModal({ claim, onClose, onSaved }) {
  const [f, setF] = useState({ amount_settled: claim.amount_claimed, settled_ref: "",
                                     status: "approved", note: "" });
  const submit = async () => {
    try {
      await api.post(`/wave4/schemes/claims/${claim.id}/reconcile`,
        { ...f, amount_settled: parseFloat(f.amount_settled) });
      toast.success("Reconciled"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Modal onClose={onClose} title={`Reconcile ${claim.claim_no}`}>
      <div className="text-sm text-[#5C5A55] mb-2">Claimed: ₹{Number(claim.amount_claimed).toLocaleString("en-IN")}</div>
      <Input placeholder="Settled amount" value={f.amount_settled} onChange={v => setF({...f, amount_settled:v})} testid="recon-amt"/>
      <Input placeholder="UTR / settled ref" value={f.settled_ref} onChange={v => setF({...f, settled_ref:v})} testid="recon-ref"/>
      <Select value={f.status} onChange={v => setF({...f, status:v})} testid="recon-status"
              options={[["approved","Approved"],["partial","Partial"],["rejected","Rejected"]]}/>
      <Input placeholder="Note" value={f.note} onChange={v => setF({...f, note:v})} testid="recon-note"/>
      <button onClick={submit} data-testid="recon-save"
              className="w-full py-2 rounded bg-[#059669] text-white">Reconcile</button>
    </Modal>
  );
}


// =====================================================
// 3. ABDM M1/M2 uplift
// =====================================================
function AbdmUpliftTab() {
  const [contexts, setContexts] = useState([]);
  const [consents, setConsents] = useState([]);
  const [txns,     setTxns]     = useState([]);
  const [showCC,   setShowCC]   = useState(false);
  const [showCon,  setShowCon]  = useState(false);

  const load = async () => {
    try {
      const [c, k, t] = await Promise.all([
        api.get("/wave4/abdm/care-contexts"),
        api.get("/wave4/abdm/consents"),
        api.get("/wave4/abdm/hi-transactions?days=30"),
      ]);
      setContexts(c.data.contexts || []);
      setConsents(k.data.consents || []);
      setTxns(t.data.transactions || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const act = async (cid, action) => {
    try {
      await api.post(`/wave4/abdm/consents/${cid}/action`, { action });
      toast.success(`Consent ${action}`); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="abdm-uplift-tab">
      <div className="p-4 rounded-lg bg-[#DBEAFE] border border-[#93C5FD] text-sm text-[#1E3A8A]">
        <b>ABDM M1/M2 uplift</b> · Care-context registration, consent artefact
        life-cycle (grant → revoke), and health-info transaction log for audit.
        Extends the ABHA links from Wave 3.
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Contexts" v={contexts.length} c="#1E3A8A"/>
        <StatTile label="Consents" v={consents.length} c="#7C2D12"/>
        <StatTile label="Granted"  v={consents.filter(c => c.status==="granted").length} c="#166534"/>
        <StatTile label="HI txns"  v={txns.length} c="#4338CA"/>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-[#8A8780]">Care contexts</div>
            <button onClick={() => setShowCC(true)} data-testid="abdm-cc-add"
              className="flex items-center gap-1 text-xs px-2 py-1 bg-[#1E3A8A] text-white rounded">
              <Plus size={12}/>Register
            </button>
          </div>
          {contexts.length === 0 ? <div className="text-sm text-[#8A8780]">No care contexts</div>
            : contexts.slice(0,10).map(c => (
              <div key={c.id} className="py-1.5 border-b last:border-0 text-sm">
                <div className="font-medium">{c.display}</div>
                <div className="text-[11px] text-[#8A8780]">{c.hi_type} · {c.reference}</div>
              </div>
            ))}
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-[#8A8780]">Consents</div>
            <button onClick={() => setShowCon(true)} data-testid="abdm-consent-add"
              className="flex items-center gap-1 text-xs px-2 py-1 bg-[#7C2D12] text-white rounded">
              <Plus size={12}/>Consent
            </button>
          </div>
          {consents.length === 0 ? <div className="text-sm text-[#8A8780]">No consents</div>
            : consents.slice(0,10).map(c => (
              <div key={c.id} className="py-2 border-b last:border-0 text-sm"
                   data-testid={`consent-row-${c.consent_no}`}>
                <div className="flex items-center justify-between">
                  <div className="font-medium">{c.consent_no}</div>
                  <StatusPill status={c.status}/>
                </div>
                <div className="text-[11px] text-[#8A8780]">{c.purpose} · {c.hi_types?.join(", ")}</div>
                {c.status === "requested" && (
                  <div className="flex gap-1 mt-1">
                    <button onClick={() => act(c.id, "grant")}
                            data-testid={`consent-grant-${c.consent_no}`}
                            className="text-[11px] px-2 py-0.5 bg-[#166534] text-white rounded">Grant</button>
                    <button onClick={() => act(c.id, "deny")}
                            className="text-[11px] px-2 py-0.5 bg-[#7C2D12] text-white rounded">Deny</button>
                  </div>
                )}
                {c.status === "granted" && (
                  <button onClick={() => act(c.id, "revoke")}
                          data-testid={`consent-revoke-${c.consent_no}`}
                          className="text-[11px] px-2 py-0.5 mt-1 bg-[#B91C1C] text-white rounded">
                    Revoke
                  </button>
                )}
              </div>
            ))}
        </div>
      </div>

      {showCC  && <CareContextModal onClose={() => setShowCC(false)}  onSaved={() => { setShowCC(false); load(); }}/>}
      {showCon && <ConsentModal     onClose={() => setShowCon(false)} onSaved={() => { setShowCon(false); load(); }}/>}
    </div>
  );
}

function CareContextModal({ onClose, onSaved }) {
  const [f, setF] = useState({ customer_id: "", reference: "", display: "",
                                     hi_type: "OPConsultation" });
  const submit = async () => {
    try {
      await api.post("/wave4/abdm/care-contexts", f);
      toast.success("Care context registered"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Modal onClose={onClose} title="Register care context">
      <Input placeholder="Customer ID" value={f.customer_id} onChange={v => setF({...f, customer_id:v})} testid="cc-cust"/>
      <Input placeholder="Reference (HSR / encounter id)" value={f.reference} onChange={v => setF({...f, reference:v})} testid="cc-ref"/>
      <Input placeholder="Display label" value={f.display} onChange={v => setF({...f, display:v})} testid="cc-disp"/>
      <Select value={f.hi_type} onChange={v => setF({...f, hi_type:v})} testid="cc-hi"
              options={[["OPConsultation","OP Consultation"],["Prescription","Prescription"],
                          ["DiagnosticReport","Diagnostic report"],["DischargeSummary","Discharge summary"],
                          ["HealthDocumentRecord","Health document"]]}/>
      <button onClick={submit} data-testid="cc-save"
              className="w-full py-2 rounded bg-[#1E3A8A] text-white">Register</button>
    </Modal>
  );
}

function ConsentModal({ onClose, onSaved }) {
  const [f, setF] = useState({ customer_id: "", requester_hip: "",
                                     purpose: "care_mgmt", hi_types: ["OPConsultation"],
                                     from_date: "", to_date: "", expires_on: "" });
  const submit = async () => {
    try {
      await api.post("/wave4/abdm/consents", f);
      toast.success("Consent artefact created"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Modal onClose={onClose} title="Create consent artefact">
      <Input placeholder="Customer ID" value={f.customer_id} onChange={v => setF({...f, customer_id:v})} testid="con-cust"/>
      <Input placeholder="Requester HIP" value={f.requester_hip} onChange={v => setF({...f, requester_hip:v})} testid="con-hip"/>
      <Select value={f.purpose} onChange={v => setF({...f, purpose:v})} testid="con-purpose"
              options={[["care_mgmt","Care management"],["self_read","Self read"],
                          ["billing","Billing"],["research","Research"]]}/>
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="From (YYYY-MM-DD)" value={f.from_date} onChange={v => setF({...f, from_date:v})} testid="con-from"/>
        <Input placeholder="To (YYYY-MM-DD)"   value={f.to_date}   onChange={v => setF({...f, to_date:v})}   testid="con-to"/>
      </div>
      <Input placeholder="Expires on (YYYY-MM-DD)" value={f.expires_on} onChange={v => setF({...f, expires_on:v})} testid="con-exp"/>
      <button onClick={submit} data-testid="con-save"
              className="w-full py-2 rounded bg-[#7C2D12] text-white">Request consent</button>
    </Modal>
  );
}


// =====================================================
// 4. Happiness Arm
// =====================================================
function HappinessTab() {
  const [analytics, setAnalytics] = useState(null);
  const [services,  setServices]  = useState([]);
  const [partners,  setPartners]  = useState([]);
  const [hsr,       setHsr]       = useState([]);
  const load = async () => {
    try {
      const [a, s, p, h] = await Promise.all([
        api.get("/wave4/happiness/analytics?days=30"),
        api.get("/wave4/happiness/services"),
        api.get("/wave4/happiness/partners"),
        api.get("/wave4/happiness/hsr?days=30"),
      ]);
      setAnalytics(a.data);
      setServices(s.data.services || []);
      setPartners(p.data.partners || []);
      setHsr(h.data.hsr || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const moodBars = useMemo(() => {
    if (!analytics) return [];
    const dist = analytics.distribution || {};
    const total = analytics.mood_entries || 1;
    return [["great","#16A34A"],["good","#84CC16"],["okay","#F59E0B"],["low","#F97316"],["awful","#B91C1C"]]
      .map(([k, c]) => ({ k, c, n: dist[k]||0, pct: Math.round(((dist[k]||0)/total)*100) }));
  }, [analytics]);

  return (
    <div className="space-y-4" data-testid="happiness-tab">
      <div className="p-4 rounded-lg bg-[#FCE7F3] border border-[#F9A8D4] text-sm text-[#831843]">
        <b>Happiness Arm</b> · Services + partners + HSR filtered to arm=happiness,
        plus a customer-facing mood diary that feeds a happiness-index score (1..5).
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Services" v={services.length} c="#BE185D"/>
        <StatTile label="Partners" v={partners.length} c="#7C3AED"/>
        <StatTile label="HSR (30d)" v={hsr.length} c="#0F766E"/>
        <StatTile label="Happiness index"
                  v={analytics?.happiness_index != null ? analytics.happiness_index : "—"}
                  c="#B91C1C"/>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white rounded-lg border p-4">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-3">Mood distribution (30d)</div>
          {(analytics?.mood_entries || 0) === 0
            ? <div className="text-sm text-[#8A8780]">No mood entries yet</div>
            : moodBars.map(b => (
              <div key={b.k} className="mb-2" data-testid={`mood-bar-${b.k}`}>
                <div className="flex justify-between text-xs mb-0.5 capitalize">
                  <span>{b.k}</span><span className="text-[#8A8780]">{b.n} · {b.pct}%</span>
                </div>
                <div className="h-2 bg-[#F5F3EE] rounded overflow-hidden">
                  <div style={{width: `${b.pct}%`, backgroundColor: b.c}} className="h-full transition-all"/>
                </div>
              </div>
            ))}
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">Recent Happiness HSR</div>
          {hsr.length === 0 ? <div className="text-sm text-[#8A8780]">No HSR yet</div>
            : hsr.slice(0,10).map(r => (
              <div key={r.id} className="flex items-center justify-between py-1.5 border-b last:border-0 text-sm">
                <div>
                  <div className="font-medium">{r.service_name || r.service_code}</div>
                  <div className="text-[11px] text-[#8A8780]">
                    {r.customer_id?.slice(0,8)} · <StatusPill status={r.status} inline/>
                  </div>
                </div>
                <Clock size={12} className="text-[#8A8780]"/>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}


// =====================================================
// Shared UI helpers
// =====================================================

// Iter 235 — searchable pill picker for large state lists
function StatePillPicker({ states, selected, onToggle }) {
  const [q, setQ] = useState("");
  const selSet = new Set(selected || []);
  const filtered = states.filter(s => !q || s.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="mb-2" data-testid="state-pill-picker">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[11px] uppercase tracking-wider text-[#8A8780]">
          Assigned states ({selected.length})
        </div>
        {selected.length > 0 && (
          <button type="button" onClick={() => selected.forEach(onToggle)}
                    data-testid="state-clear"
                    className="text-[10px] text-[#B91C1C]">Clear all</button>
        )}
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2 p-2 bg-[#F5F3EE] rounded border">
          {selected.map(id => {
            const s = states.find(x => x.id === id);
            return (
              <button key={id} type="button" onClick={() => onToggle(id)}
                        data-testid={`state-pill-${id.slice(0,6)}`}
                        className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-[#0F766E] text-white text-[11px] hover:bg-[#0B5C56]">
                {s?.name || id.slice(0,8)}
                <span className="ml-1 w-4 h-4 rounded-full bg-white/20 flex items-center justify-center text-[10px]">×</span>
              </button>
            );
          })}
        </div>
      )}
      <input placeholder={`Search ${states.length} states…`}
               value={q} onChange={e => setQ(e.target.value)}
               data-testid="state-search"
               className="w-full border rounded px-3 py-1.5 text-sm mb-1"/>
      <div className="max-h-40 overflow-y-auto border rounded p-1">
        {filtered.length === 0
          ? <div className="text-xs text-[#8A8780] p-2">
              {states.length === 0
                ? "No states in the locations tree — seed one first."
                : "No matches"}
            </div>
          : filtered.map(s => {
              const on = selSet.has(s.id);
              return (
                <button key={s.id} type="button" onClick={() => onToggle(s.id)}
                          data-testid={`state-opt-${s.id.slice(0,6)}`}
                          className={`w-full flex items-center justify-between px-2 py-1 rounded text-xs text-left ${
                            on ? "bg-[#0F766E]/10 text-[#0F766E]" : "hover:bg-[#F5F3EE]"}`}>
                  <span>{s.name}</span>
                  {on && <span className="text-[10px]">✓</span>}
                </button>
              );
            })}
      </div>
    </div>
  );
}

function Input({ placeholder, value, onChange, testid }) {
  return (
    <input placeholder={placeholder} value={value || ""}
           onChange={e => onChange(e.target.value)}
           data-testid={testid}
           className="w-full border rounded px-3 py-2 mb-2 text-sm"/>
  );
}
function Select({ value, onChange, options, testid }) {
  return (
    <select value={value || ""} onChange={e => onChange(e.target.value)}
            data-testid={testid}
            className="w-full border rounded px-3 py-2 mb-2 text-sm bg-white">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}
function StatTile({ label, v, c }) {
  return (
    <div className="p-4 bg-white rounded-lg border">
      <div className="text-[24px] font-heading font-semibold" style={{color:c}}>{v}</div>
      <div className="text-[11px] uppercase tracking-[0.15em] text-[#8A8780] mt-1">{label}</div>
    </div>
  );
}
function StatusPill({ status, inline }) {
  const palette = {
    // aircraft
    available: "#166534", in_flight: "#B45309", maintenance: "#7C2D12", grounded: "#7F1D1D",
    // missions
    requested: "#4338CA", dispatched: "#B45309", completed: "#166534",
    // consents
    granted: "#166534", denied: "#7F1D1D", revoked: "#7F1D1D",
    // claims
    submitted: "#4338CA", approved: "#166534", partial: "#B45309", rejected: "#7F1D1D",
  };
  return (
    <span className={`${inline?"":"px-2 py-0.5"} rounded text-[10px] uppercase tracking-wider font-medium`}
          style={{color: palette[status] || "#5C5A55",
                    backgroundColor: inline ? "transparent" : (palette[status] || "#8A8780")+"22"}}>
      {status}
    </span>
  );
}
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full p-5 max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg">{title}</h2>
          <button onClick={onClose} className="text-2xl leading-none">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
